/**
 * CSV parse + payload helpers for roster bulk class import (POST /v1/roster/classes/bulk).
 * Unit tests optional — see repo test setup.
 */
import type { RosterBulkCreateClassItem } from '../../modules/roster/api/roster.client';

/** Canonical CSV headers aligned with RosterBulkCreateClassItem (no section — not in bulk row contract). */
export const ROSTER_BULK_CANONICAL_HEADERS = ['name', 'grade', 'academic_year_key', 'status'] as const;

/**
 * Map alternate header names to canonical keys after lowercasing.
 * `academic_year` is accepted for spreadsheets that used the old platform-oriented name.
 */
const HEADER_ALIASES: Record<string, string> = {
  academic_year: 'academic_year_key',
};

export interface ParsedCsvRow {
  /** 1-based line index in file (data rows) for display; backend may use 0-based index in results. */
  sourceRowIndex: number;
  item: RosterBulkCreateClassItem;
}

export interface ParseBulkClassesCsvResult {
  rows: ParsedCsvRow[];
  warnings: string[];
  errors: string[];
}

export function parseCsvRaw(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let i = 0;
  let inQuotes = false;

  const pushField = () => {
    row.push(field);
    field = '';
  };

  const pushRow = () => {
    rows.push(row);
    row = [];
  };

  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (c === ',') {
      pushField();
      i++;
      continue;
    }
    if (c === '\r') {
      i++;
      continue;
    }
    if (c === '\n') {
      pushField();
      pushRow();
      i++;
      continue;
    }
    field += c;
    i++;
  }
  pushField();
  if (row.length > 1 || (row.length === 1 && row[0].trim() !== '')) {
    pushRow();
  }
  return rows;
}

function normalizeHeader(h: string): string {
  const t = h.trim().toLowerCase();
  return HEADER_ALIASES[t] ?? t;
}

function isRowBlank(cells: string[]): boolean {
  return cells.every((c) => !String(c).trim());
}

const CANONICAL = new Set<string>(ROSTER_BULK_CANONICAL_HEADERS);

export function parseBulkClassesCsv(text: string): ParseBulkClassesCsvResult {
  const warnings: string[] = [];
  const errors: string[] = [];
  const grid = parseCsvRaw(text.replace(/^\uFEFF/, ''));
  if (grid.length === 0) {
    errors.push('The file is empty.');
    return { rows: [], warnings, errors };
  }

  const headerCells = grid[0].map((h) => normalizeHeader(h));
  const indexByCanon: Record<string, number> = {};
  const unknownHeaders: string[] = [];

  headerCells.forEach((h, idx) => {
    if (!h) return;
    if (CANONICAL.has(h)) {
      if (indexByCanon[h] === undefined) indexByCanon[h] = idx;
    } else if (h === 'code' || h === 'section') {
      unknownHeaders.push(h);
    } else {
      unknownHeaders.push(h);
    }
  });

  if (unknownHeaders.length) {
    warnings.push(`Ignored column(s) not used by roster bulk import: ${[...new Set(unknownHeaders)].join(', ')}.`);
  }
  if (indexByCanon.name === undefined) {
    errors.push('Missing required column: name.');
  }
  if (indexByCanon.grade === undefined) {
    errors.push('Missing required column: grade.');
  }

  if (errors.length) {
    return { rows: [], warnings, errors };
  }

  const rows: ParsedCsvRow[] = [];

  for (let r = 1; r < grid.length; r++) {
    const line = grid[r];
    if (isRowBlank(line)) continue;

    const name = (line[indexByCanon.name] ?? '').trim();
    if (!name) {
      errors.push(`Row ${r + 1}: name is required (or remove blank rows).`);
      continue;
    }
    if (!/\S/.test(name)) {
      errors.push(`Row ${r + 1}: name cannot be only whitespace.`);
      continue;
    }

    const grade = (line[indexByCanon.grade] ?? '').trim();
    if (!grade) {
      errors.push(`Row ${r + 1}: grade is required.`);
      continue;
    }

    const ayRaw =
      indexByCanon.academic_year_key !== undefined ? (line[indexByCanon.academic_year_key] ?? '').trim() : '';
    const statusRaw = indexByCanon.status !== undefined ? (line[indexByCanon.status] ?? '').trim() : '';

    const item: RosterBulkCreateClassItem = {
      name,
      grade,
      ...(ayRaw ? { academic_year_key: ayRaw } : {}),
      ...(statusRaw ? { status: statusRaw } : {}),
    };
    rows.push({ sourceRowIndex: r + 1, item });
  }

  if (rows.length === 0 && errors.length === 0) {
    errors.push('No data rows found after the header.');
  }

  return { rows, warnings, errors };
}

export function buildRosterBulkPayloadFromParsed(
  rows: ParsedCsvRow[],
  skip_duplicates: boolean,
  continue_on_error: boolean
) {
  return {
    classes: rows.map((r) => r.item),
    skip_duplicates,
    continue_on_error,
  };
}
